<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">{{ $t("Subscription Plans") }}</h4>
        <b-button variant="primary" @click="openCreateModal">
          <feather-icon icon="PlusIcon" />
          {{ $t("New Plan") }}
        </b-button>
      </div>

      <b-alert show variant="warning" class="mt-1" v-if="hasCheckoutPlan === false">
        {{ $t("No launch promotion plan is configured. New subscription checkout will fail.") }}
      </b-alert>

      <b-table
        small
        responsive
        hover
        class="mt-1"
        :items="plans"
        :fields="fields"
        primary-key="_id"
        :busy="loading"
      >
        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <span>{{ data.item.name }}</span>
            <b-badge
              v-if="data.item.availableForNewCheckout === false"
              variant="secondary"
              class="ml-1"
            >
              {{ $t("subscriptionPlans.closedForNewSales") }}
            </b-badge>
            <b-badge v-if="data.item.key === 'launch_promo'" variant="success" class="ml-1">
              {{ $t("Launch Promotion") }}
            </b-badge>
            <b-badge v-if="data.item.key === 'default'" variant="primary" class="ml-1">
              {{ $t("Default") }}
            </b-badge>
            <b-badge v-if="data.item.isTestPlan" variant="info" class="ml-1">
              {{ $t("Test") }}
            </b-badge>
          </div>
        </template>
        <template #cell(price)="data">
          <span>{{ data.item.price }} {{ data.item.currency || "TRY" }}</span>
        </template>
        <template #cell(interval)="data">
          <span>
            {{ data.item.paymentIntervalCount || 1 }} × {{ data.item.paymentInterval }}
          </span>
        </template>
        <template #cell(trial)="data">
          <span>{{ data.item.trialPeriodDays || 0 }}d</span>
        </template>
        <template #cell(createdAt)="data">
          <span>{{ formatDate(data.item.createdAt) }}</span>
        </template>
        <template #cell(actions)="data">
          <span v-if="data.item.key" class="text-muted">
            {{ $t("Configured") }}
          </span>
          <span v-else class="text-muted">{{ $t("Not used by checkout") }}</span>
        </template>
      </b-table>
    </b-card>

    <b-modal
      ref="planModal"
      id="planModal"
      centered
      hide-footer
      @hide="resetForm"
      :title="$t('New Subscription Plan')"
    >
      <b-row class="mt-1 align-items-center">
        <b-col sm="4"><label>{{ $t("Name") }}</label></b-col>
        <b-col sm="8">
          <b-form-input v-model="form.name" :placeholder="$t('Name')" />
        </b-col>
      </b-row>
      <b-row class="mt-1 align-items-center">
        <b-col sm="4"><label>{{ $t("Price") }} (TRY)</label></b-col>
        <b-col sm="8">
          <b-form-input type="number" min="1" v-model.number="form.price" />
        </b-col>
      </b-row>
      <b-row class="mt-1 align-items-center">
        <b-col sm="4"><label>{{ $t("Trial Period Days") }}</label></b-col>
        <b-col sm="8">
          <b-form-input type="number" min="0" v-model.number="form.trialPeriodDays" />
        </b-col>
      </b-row>
      <b-row class="mt-1 align-items-center">
        <b-col sm="4"><label>{{ $t("Payment Interval") }}</label></b-col>
        <b-col sm="8">
          <b-form-select v-model="form.paymentInterval" :options="intervalOptions" />
        </b-col>
      </b-row>
      <b-row class="mt-1 align-items-center">
        <b-col sm="4"><label>{{ $t("Interval Count") }}</label></b-col>
        <b-col sm="8">
          <b-form-input type="number" min="1" v-model.number="form.paymentIntervalCount" />
        </b-col>
      </b-row>
      <b-alert show variant="info" class="mt-2 mb-0">
        {{ $t("Only the approved Default and Launch Promotion plans are used by checkout.") }}
      </b-alert>
      <div class="mt-2 d-flex justify-content-end">
        <b-button variant="secondary" class="mr-1" @click="$refs.planModal.hide()">
          {{ $t("Cancel") }}
        </b-button>
        <b-button variant="primary" :disabled="creating" @click="createPlan">
          <b-spinner v-if="creating" small /> {{ $t("Create Plan") }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BButton,
  BModal,
  BRow,
  BCol,
  BFormInput,
  BFormSelect,
  BBadge,
  BAlert,
  BSpinner,
} from "bootstrap-vue";
import axios from "@axios";
const {
  axiosRoutes: {
    adminSubscriptionPlan: { getAll: apiGetAll, create: apiCreate },
  },
} = require("/G_CONFIG");

export default {
  components: {
    BCard,
    BTable,
    BButton,
    BModal,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BBadge,
    BAlert,
    BSpinner,
  },
  data() {
    return {
      plans: [],
      loading: false,
      creating: false,
      form: this.emptyForm(),
      intervalOptions: [
        { value: "MONTHLY", text: this.$t("Monthly") },
        { value: "YEARLY", text: this.$t("Yearly") },
        { value: "WEEKLY", text: this.$t("Weekly") },
        { value: "DAILY", text: this.$t("Daily") },
      ],
      fields: [
        { key: "name", label: this.$t("Name") },
        { key: "price", label: this.$t("Price") },
        { key: "interval", label: this.$t("Interval") },
        { key: "trial", label: this.$t("Trial") },
        { key: "createdAt", label: this.$t("Created") },
        { key: "actions", label: this.$t("Actions") },
      ],
    };
  },
  computed: {
    hasCheckoutPlan() {
      if (!this.plans || !this.plans.length) return null;
      return this.plans.some(p => p.key === 'launch_promo')
    },
  },
  mounted() {
    this.loadPlans();
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        price: null,
        trialPeriodDays: 0,
        paymentInterval: "MONTHLY",
        paymentIntervalCount: 1,
      };
    },
    resetForm() {
      this.form = this.emptyForm();
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString();
    },
    async loadPlans() {
      this.loading = true;
      try {
        const { data } = await axios.get(apiGetAll);
        this.plans = data;
      } catch (err) {
        this.toastErr(err, this.$t("Failed to load plans"));
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.resetForm();
      this.$refs.planModal.show();
    },
    async createPlan() {
      if (!this.form.name || !this.form.price || this.form.price <= 0) {
        this.$bvToast.toast(this.$t("Name and positive price are required"), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }
      this.creating = true;
      try {
        await axios.post(apiCreate, this.form);
        this.$bvToast.toast(this.$t("Plan created"), {
          title: this.$t("Success"),
          variant: "success",
          solid: true,
        });
        this.$refs.planModal.hide();
        await this.loadPlans();
      } catch (err) {
        this.toastErr(err, this.$t("Failed to create plan"));
      } finally {
        this.creating = false;
      }
    },
    toastErr(err, fallback) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        fallback;
      this.$bvToast.toast(msg, {
        title: this.$t("Error"),
        variant: "danger",
        solid: true,
      });
    },
  },
};
</script>
